import React, {
  forwardRef,
  useState,
  useRef,
  useLayoutEffect,
  useImperativeHandle,
  useCallback,
  useMemo,
  useEffect
} from 'react';
import type { Key, RefAttributes } from 'react';
import clsx from 'clsx';

import { rootClassname, viewportDraggingClassname, focusSinkClassname } from './style';
import {
  useGridDimensions,
  useCalculatedColumns,
  useViewportColumns,
  useViewportRows,
  useLatestFunc,
  RowSelectionChangeProvider
} from './hooks';
import HeaderRow from './HeaderRow';
import Row from './Row';
import GroupRowRenderer from './GroupRow';
import SummaryRow from './SummaryRow';
import {
  assertIsValidKeyGetter,
  onEditorNavigation,
  getNextSelectedCellPosition,
  isSelectedCellEditable,
  canExitGrid,
  isCtrlKeyHeldDown,
  isDefaultCellInput,
  getColSpan,
  max,
  sign
} from './utils';

import type {
  CalculatedColumn,
  Column,
  GridSelection,
  Position,
  RowRendererProps,
  RowsChangeData,
  SelectRowEvent,
  SelectedCellProps,
  EditCellProps,
  FillEvent,
  PasteEvent,
  CellNavigationMode,
  SortColumn,
  RowHeightArgs,
  CellStyles,
  DragPosition,
  TSelection,
  ScrollDragOptions,
} from './types';

interface SelectCellState extends Position {
  readonly mode: 'SELECT';
}

interface EditCellState<R> extends Position {
  readonly mode: 'EDIT';
  readonly row: R;
  readonly originalRow: R;
  readonly key: string | null;
}

type DefaultColumnOptions<R, SR> = Pick<
  Column<R, SR>,
  'formatter' | 'minWidth' | 'resizable' | 'sortable'
>;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
const body = globalThis.document?.body;

const initialPosition: SelectCellState = {
  idx: -1,
  rowIdx: -1,
  mode: 'SELECT'
};

export interface DataGridHandle {
  element: HTMLDivElement | null;
  scrollToColumn: (colIdx: number) => void;
  scrollToRow: (rowIdx: number) => void;
  focusGrid: () => void;
  selectCell: (position: Position, enableEditor?: boolean | null) => void;
}

type SharedDivProps = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'aria-label' | 'aria-labelledby' | 'aria-describedby' | 'className' | 'style'
>;

export interface DataGridProps<R, SR = unknown, K extends Key = Key> extends SharedDivProps {
  /**
   * Grid and data Props
   */
  /** An array of objects representing each column on the grid */
  columns: readonly Column<R, SR>[];
  /** A function called for each rendered row that should return a plain key/value pair object */
  rows: readonly R[];
  /**
   * Rows to be pinned at the bottom of the rows view for summary, the vertical scroll bar will not scroll these rows.
   * Bottom horizontal scroll bar can move the row left / right. Or a customized row renderer can be used to disabled the scrolling support.
   */
  summaryRows?: readonly SR[] | null;
  /** The getter should return a unique key for each row */
  rowKeyGetter?: ((row: R) => K) | null;
  onRowsChange?: ((rows: R[], data: RowsChangeData<R, SR>) => void) | null;

  /**
   * Dimensions props
   */
  /** The height of each row in pixels */
  rowHeight?: number | ((args: RowHeightArgs<R>) => number) | null;
  /** The height of the header row in pixels */
  headerRowHeight?: number | null;
  /** The height of each summary row in pixels */
  summaryRowHeight?: number | null;

  /**
   * Feature props
   */
  /** Set of selected row keys */
  selectedRows?: ReadonlySet<K> | null;
  /** Function called whenever row selection is changed */
  onSelectedRowsChange?: ((selectedRows: Set<K>) => void) | null;
  /**Used for multi column sorting */
  sortColumns?: readonly SortColumn[] | null;
  onSortColumnsChange?: ((sortColumns: SortColumn[]) => void) | null;
  defaultColumnOptions?: DefaultColumnOptions<R, SR> | null;
  groupBy?: readonly string[] | null;
  rowGrouper?: ((rows: readonly R[], columnKey: string) => Record<string, readonly R[]>) | null;
  expandedGroupIds?: ReadonlySet<unknown> | null;
  onExpandedGroupIdsChange?: ((expandedGroupIds: Set<unknown>) => void) | null;
  onFill?: ((event: FillEvent<R>) => R[] | null) | null;
  onPaste?: ((event: PasteEvent<R>) => R) | null;
  cellStyles?: Record<number, Record<number, CellStyles>>;
  reorderRows?: boolean;
  scrollDragOptions?: ScrollDragOptions;

  /**
   * Custom renderers
   */
  rowRenderer?: React.ComponentType<RowRendererProps<R, SR>> | null;
  emptyRowsRenderer?: React.ComponentType | null;

  /**
   * Event props
   */
  /** Function called whenever a row is clicked */
  onRowClick?: ((row: R, column: CalculatedColumn<R, SR>) => void) | null;
  /** Function called whenever a row is double clicked */
  onRowDoubleClick?: ((row: R, column: CalculatedColumn<R, SR>) => void) | null;
  /** Called when the grid is scrolled */
  onScroll?: ((event: React.UIEvent<HTMLDivElement>) => void) | null;
  /** Called when a column is resized */
  onColumnResize?: (idx: number, width: number, mouseDown: boolean) => void;
  /** Function called whenever selected cell is changed */
  onSelectedCellChange?: ((position: Position) => void) | null;

  /**
   * Toggles and modes
   */
  cellNavigationMode?: CellNavigationMode | null;
  enableVirtualization?: boolean | null;

  /**
   * Miscellaneous
   */
  /** The node where the editor portal should mount. */
  editorPortalTarget?: Element | null;
  rowClass?: ((row: R) => string | undefined | null) | null;
}

/**
 * Main API Component to render a data grid of rows and columns
 *
 * @example
 *
 * <DataGrid columns={columns} rows={rows} />
 */
function DataGrid<R, SR, K extends Key>(
  {
    // Grid and data Props
    columns: rawColumns,
    rows: rawRows,
    summaryRows,
    rowKeyGetter,
    onRowsChange,
    // Dimensions props
    rowHeight,
    headerRowHeight: rawHeaderRowHeight,
    summaryRowHeight: rawSummaryRowHeight,
    // Feature props
    selectedRows,
    onSelectedRowsChange,
    sortColumns,
    onSortColumnsChange,
    defaultColumnOptions,
    groupBy: rawGroupBy,
    rowGrouper,
    expandedGroupIds,
    onExpandedGroupIdsChange,
    // Custom renderers
    rowRenderer,
    emptyRowsRenderer: EmptyRowsRenderer,
    // Event props
    onRowClick,
    onRowDoubleClick,
    onScroll,
    onColumnResize,
    onSelectedCellChange,
    onFill,
    onPaste,
    // Toggles and modes
    cellNavigationMode: rawCellNavigationMode,
    enableVirtualization,
    // Miscellaneous
    editorPortalTarget: rawEditorPortalTarget,
    className,
    style,
    rowClass,
    cellStyles,
    reorderRows = false,
    scrollDragOptions = {},
    // ARIA
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy
  }: DataGridProps<R, SR, K>,
  ref: React.Ref<DataGridHandle>
) {
  /**
   * defaults
   */
  rowHeight ??= 20;
  const headerRowHeight = rawHeaderRowHeight ?? (typeof rowHeight === 'number' ? rowHeight : 30);
  const summaryRowHeight = rawSummaryRowHeight ?? (typeof rowHeight === 'number' ? rowHeight : 45);
  const RowRenderer = rowRenderer ?? Row;
  const cellNavigationMode = rawCellNavigationMode ?? 'NONE';
  enableVirtualization ??= true;
  const editorPortalTarget = rawEditorPortalTarget ?? body;
  /**
   * states
   */
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [columnWidths, setColumnWidths] = useState<ReadonlyMap<string, number>>(() => new Map());
  const [selectedPosition, setSelectedPosition] = useState<SelectCellState | EditCellState<R>>(
    initialPosition
  );
  const [copiedCell, setCopiedCell] = useState<{ row: R; columnKey: string } | null>(null);
  const [isDragging, setDragging] = useState(false);
  const [draggedOverRange, setDraggedOverRange] = useState<(number[] | undefined)[]>([]);

  /**
   * refs
   */
  const focusSinkRef = useRef<HTMLDivElement>(null);
  const prevSelectedPosition = useRef(selectedPosition);
  const lastSelectedRowIdx = useRef(-1);
  const isCellFocusable = useRef(false);
  const dragPos = useRef<DragPosition<R, SR>>();
  const dragStarted = useRef(false);
  const updateScrollInterval = useRef<ReturnType<typeof setInterval>>();
  const progScroll = useRef<{ top: number, bottom: number, right: number, left: number }>({ top: 1, bottom: 1, right: 1, left: 1 });

  /**
   * computed values
   */
  const [gridRef, gridWidth, gridHeight] = useGridDimensions();
  const headerRowsCount = 1;
  const summaryRowsCount = summaryRows?.length ?? 0;
  const clientHeight = gridHeight - headerRowHeight - summaryRowsCount * summaryRowHeight;
  const isSelectable = selectedRows != null && onSelectedRowsChange != null;

  const allRowsSelected = useMemo((): boolean => {
    // no rows to select = explicitely unchecked
    const { length } = rawRows;
    return (
      length !== 0 &&
      selectedRows != null &&
      rowKeyGetter != null &&
      selectedRows.size >= length &&
      rawRows.every((row) => selectedRows.has(rowKeyGetter(row)))
    );
  }, [rawRows, selectedRows, rowKeyGetter]);

  const {
    columns,
    colSpanColumns,
    colOverscanStartIdx,
    colOverscanEndIdx,
    layoutCssVars,
    columnMetrics,
    totalColumnWidth,
    lastFrozenColumnIndex,
    totalFrozenColumnWidth,
    groupBy
  } = useCalculatedColumns({
    rawColumns,
    columnWidths,
    scrollLeft,
    viewportWidth: gridWidth,
    defaultColumnOptions,
    rawGroupBy: rowGrouper ? rawGroupBy : undefined,
    enableVirtualization
  });

  /**
   * VS - Reset column widths when columns update
   * Key based persisted widths break having modules with different widths   
   */
  useEffect(() => {
    setColumnWidths(new Map());
  }, [columns]);

  useEffect(() => {
    setColumnWidths(new Map());
  }, [isDragging]);

  const {
    rowOverscanStartIdx,
    rowOverscanEndIdx,
    rows,
    rowsCount,
    totalRowHeight,
    isGroupRow,
    getRowTop,
    getRowHeight,
    findRowIdx
  } = useViewportRows({
    rawRows,
    groupBy,
    rowGrouper,
    rowHeight,
    clientHeight,
    scrollTop,
    expandedGroupIds,
    enableVirtualization
  });

  const viewportColumns = useViewportColumns({
    columns,
    colSpanColumns,
    colOverscanStartIdx,
    colOverscanEndIdx,
    lastFrozenColumnIndex,
    rowOverscanStartIdx,
    rowOverscanEndIdx,
    rows,
    summaryRows,
    isGroupRow
  });

  const hasGroups = groupBy.length > 0 && typeof rowGrouper === 'function';
  const minColIdx = 0;
  const minSelIdx = 1;

  // Cell drag is not supported on a treegrid
  const enableCellDragAndDrop = hasGroups ? false : onFill != null;

  /**
   * The identity of the wrapper function is stable so it won't break memoization
   */
  const selectRowLatest = useLatestFunc(selectRow);
  const selectAllRowsLatest = useLatestFunc(selectAllRows);
  const selectCellLatest = useLatestFunc(
    (row: R, column: CalculatedColumn<R, SR>, enableEditor: boolean | undefined | null) => {
      const rowIdx = rows.indexOf(row);
      selectCell({ rowIdx, idx: column.idx }, enableEditor);
    }
  );
  const selectGroupLatest = useLatestFunc((rowIdx: number) => {
    selectCell({ rowIdx, idx: -1 });
  });
  const toggleGroupLatest = useLatestFunc(toggleGroup);

  /**
   * effects
   */
  useLayoutEffect(() => {
    if (
      selectedPosition === prevSelectedPosition.current ||
      selectedPosition.mode === 'EDIT' ||
      !isCellWithinBounds(selectedPosition)
    ) {
      return;
    }

    if (selectedPosition.sel && selectedPosition.sel !== prevSelectedPosition.current.sel) {
      let newSelIdx; let newSelRowIdx;
      if (!prevSelectedPosition.current.sel) {
        newSelIdx = selectedPosition.idx === selectedPosition.sel.colStart ? selectedPosition.sel.colEnd : selectedPosition.sel.colStart;
        newSelRowIdx = selectedPosition.rowIdx === selectedPosition.sel.rowStart ? selectedPosition.sel.rowEnd : selectedPosition.sel.rowStart;
      } else {
        newSelIdx = selectedPosition.sel.colStart !== prevSelectedPosition.current.sel.colStart ? selectedPosition.sel.colStart : selectedPosition.sel.colEnd;
        newSelRowIdx = selectedPosition.sel.rowStart !== prevSelectedPosition.current.sel.rowStart ? selectedPosition.sel.rowStart : selectedPosition.sel.rowEnd;
      }
      const isFullSelection =
        (selectedPosition.sel.colStart === selectedPosition.sel.colEnd && selectedPosition.sel.rowStart === 0 && selectedPosition.sel.rowEnd === rowsCount - 1)
        || (selectedPosition.sel.rowStart === selectedPosition.sel.rowEnd && selectedPosition.sel.colStart === 1 && selectedPosition.sel.colEnd === columns.length - 1)
        || (selectedPosition.sel.rowStart === 0 && selectedPosition.sel.rowEnd === rowsCount - 1 && selectedPosition.sel.colStart === 0 && selectedPosition.sel.colEnd === columns.length - 1);
      if (!isFullSelection) scrollToCell({ idx: newSelIdx, rowIdx: newSelRowIdx });
    } else {
      scrollToCell(selectedPosition);
    }
    prevSelectedPosition.current = selectedPosition;

    if (isCellFocusable.current) {
      isCellFocusable.current = false;
      return;
    }
    focusSinkRef.current!.focus({ preventScroll: true });
  });

  useImperativeHandle(ref, () => ({
    element: gridRef.current,
    scrollToColumn(idx: number) {
      scrollToCell({ idx });
    },
    scrollToRow(rowIdx: number) {
      const { current } = gridRef;
      if (!current) return;
      current.scrollTo({
        top: getRowTop(rowIdx),
        behavior: 'smooth'
      });
    },
    selectCell,
    focusGrid
  }));

  const updateEdgeScroll = useCallback((e: MouseEvent) => {
    if (!dragPos.current) return;
    const { lastRowIdx, lastIdx } = dragPos.current;
    if (typeof lastIdx === "undefined" || typeof lastRowIdx === "undefined") return;
    const { current } = gridRef;
    if (!current) return;

    const isCellAtLeftBoundary = e.clientX <= totalFrozenColumnWidth + current.offsetLeft + 20;
    const isCellAtRightBoundary = e.clientX >= window.innerWidth - 20;
    const isCellAtTopBoundary = getRowTop(lastRowIdx) - current.scrollTop <= 20;
    const isCellAtBottomBoundary = e.clientY >= window.innerHeight - 15;

    const isBoundary = isCellAtBottomBoundary || isCellAtLeftBoundary || isCellAtRightBoundary || isCellAtTopBoundary;

    if (!updateScrollInterval.current && (isBoundary)) {
      const { maxPixels = 60, baseSpeed = 10, acceleration = 1.01 } = scrollDragOptions;
      updateScrollInterval.current = setInterval(() => {
        if (isCellAtBottomBoundary) {
          current.scrollTop += progScroll.current.bottom;
          progScroll.current.bottom = Math.min(progScroll.current.bottom * acceleration, maxPixels);
        } else progScroll.current.bottom = 1;
        if (isCellAtRightBoundary) {
          current.scrollLeft += progScroll.current.right;
          progScroll.current.right = Math.min(progScroll.current.right * acceleration, maxPixels);
        } else progScroll.current.right = 1;
        if (isCellAtTopBoundary) {
          current.scrollTop -= progScroll.current.top;
          progScroll.current.top = Math.min(progScroll.current.top * acceleration, maxPixels);
        } else progScroll.current.top = 1;
        if (isCellAtLeftBoundary) {
          current.scrollLeft -= progScroll.current.left;
          progScroll.current.left = Math.min(progScroll.current.left * acceleration, maxPixels);
        } else progScroll.current.left = 1;
      }, baseSpeed);
    }

    if (!isBoundary) {
      clearInterval(updateScrollInterval.current);
      updateScrollInterval.current = undefined;
      progScroll.current = { top: 1, bottom: 1, left: 1, right: 1 };
    }
  }, [getRowTop, gridRef, totalFrozenColumnWidth]);

  useEffect(() => {
    window.addEventListener('mousemove', updateEdgeScroll);

    return () => {
      window.removeEventListener('mousemove', updateEdgeScroll);
    }
  }, []);

  /**
   * callbacks
   */
  const handleColumnResize = useCallback(
    (column: CalculatedColumn<R, SR>, width: number, mouseDown: boolean) => {
      setColumnWidths((columnWidths) => {
        const newColumnWidths = new Map(columnWidths);
        newColumnWidths.set(column.key, width);
        return newColumnWidths;
      });
      onColumnResize?.(column.idx, width, mouseDown);
    },
    [onColumnResize]
  );

  /**
   * event handlers
   */
  function selectRow({ row, checked, isShiftClick }: SelectRowEvent<R>) {
    if (!onSelectedRowsChange) return;

    assertIsValidKeyGetter<R, K>(rowKeyGetter);
    const newSelectedRows = new Set(selectedRows);
    if (isGroupRow(row)) {
      for (const childRow of row.childRows) {
        const rowKey = rowKeyGetter(childRow);
        if (checked) {
          newSelectedRows.add(rowKey);
        } else {
          newSelectedRows.delete(rowKey);
        }
      }
      onSelectedRowsChange(newSelectedRows);
      return;
    }

    const rowKey = rowKeyGetter(row);
    if (checked) {
      newSelectedRows.add(rowKey);
      const previousRowIdx = lastSelectedRowIdx.current;
      const rowIdx = rows.indexOf(row);
      lastSelectedRowIdx.current = rowIdx;
      if (isShiftClick && previousRowIdx !== -1 && previousRowIdx !== rowIdx) {
        const step = sign(rowIdx - previousRowIdx);
        for (let i = previousRowIdx + step; i !== rowIdx; i += step) {
          const row = rows[i];
          if (isGroupRow(row)) continue;
          newSelectedRows.add(rowKeyGetter(row));
        }
      }
    } else {
      newSelectedRows.delete(rowKey);
      lastSelectedRowIdx.current = -1;
    }

    onSelectedRowsChange(newSelectedRows);
  }

  function selectAllRows(checked: boolean) {
    if (!onSelectedRowsChange) return;

    assertIsValidKeyGetter<R, K>(rowKeyGetter);
    const newSelectedRows = new Set(selectedRows);

    for (const row of rawRows) {
      const rowKey = rowKeyGetter(row);
      if (checked) {
        newSelectedRows.add(rowKey);
      } else {
        newSelectedRows.delete(rowKey);
      }
    }

    onSelectedRowsChange(newSelectedRows);
  }

  function toggleGroup(expandedGroupId: unknown) {
    if (!onExpandedGroupIdsChange) return;
    const newExpandedGroupIds = new Set(expandedGroupIds);
    if (newExpandedGroupIds.has(expandedGroupId)) {
      newExpandedGroupIds.delete(expandedGroupId);
    } else {
      newExpandedGroupIds.add(expandedGroupId);
    }
    onExpandedGroupIdsChange(newExpandedGroupIds);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const { key, keyCode } = event;
    const row = rows[selectedPosition.rowIdx];
    const ctrlKey = isCtrlKeyHeldDown(event);

    if (selectedPosition.mode === 'EDIT') {
      if (event.key === 'Escape') closeEditor();
      else if (!ctrlKey) handleCellInput(event);
      return;
    }

    if (
      onPaste &&
      isCtrlKeyHeldDown(event) &&
      isCellWithinBounds(selectedPosition) &&
      !isGroupRow(row) &&
      selectedPosition.idx !== -1 &&
      selectedPosition.mode === 'SELECT'
    ) {
      // event.key may differ by keyboard input language, so we use event.keyCode instead
      // event.nativeEvent.code cannot be used either as it would break copy/paste for the DVORAK layout
      const cKey = 67;
      const vKey = 86;
      if (keyCode === cKey) {
        handleCopy();
        return;
      }
      if (keyCode === vKey) {
        handlePaste();
        return;
      }
    }

    if (
      isCellWithinBounds(selectedPosition) &&
      isGroupRow(row) &&
      selectedPosition.idx === -1 &&
      // Collapse the current group row if it is focused and is in expanded state
      ((key === 'ArrowLeft' && row.isExpanded) ||
        // Expand the current group row if it is focused and is in collapsed state
        (key === 'ArrowRight' && !row.isExpanded))
    ) {
      event.preventDefault(); // Prevents scrolling
      toggleGroup(row.id);
      return;
    }

    switch (event.key) {
      case 'Escape':
        if (selectedPosition.sel) {
          const newSelPos = { ...selectedPosition, idx: selectedPosition.idx || 1 };
          delete newSelPos.sel;
          selectCell(newSelPos);
        }
        setCopiedCell(null);
        return;
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Tab':
      case 'Home':
      case 'End':
      case 'PageUp':
      case 'PageDown':
        if (ctrlKey) processHotkey(event);
        else navigate(event);
        break;
      case ' ':
        if ((event.shiftKey || ctrlKey) && event.key === ' ') processHotkey(event);
        else handleCellInput(event);
        break;
      case 'a':
        if (ctrlKey) processHotkey(event);
        else handleCellInput(event);
        break;
      default:
        if (!ctrlKey) handleCellInput(event);
        break;
    }
  }

  function handleFocus() {
    isCellFocusable.current = true;
  }

  function focusGrid() {
    focusSinkRef.current!.focus();
  }

  function handleScroll(event: React.UIEvent<HTMLDivElement>) {
    const { scrollTop, scrollLeft } = event.currentTarget;
    setScrollTop(scrollTop);
    setScrollLeft(scrollLeft);
    onScroll?.(event);
  }

  const getRawRowIdx = useCallback((rowIdx: number) => {
    return hasGroups ? rawRows.indexOf(rows[rowIdx] as R) : rowIdx;
  }, [hasGroups, rawRows, rows]);

  function handleCopy() {
    const { idx, rowIdx } = selectedPosition;
    setCopiedCell({ row: rawRows[getRawRowIdx(rowIdx)], columnKey: columns[idx].key });
  }

  function handlePaste() {
    const { idx, rowIdx } = selectedPosition;
    const targetRow = rawRows[getRawRowIdx(rowIdx)];
    if (!onPaste || !onRowsChange || copiedCell === null || !isCellEditable(selectedPosition)) {
      return;
    }

    const updatedTargetRow = onPaste({
      sourceRow: copiedCell.row,
      sourceColumnKey: copiedCell.columnKey,
      targetRow,
      targetColumnKey: columns[idx].key
    });

    updateRow(rowIdx, updatedTargetRow);
  }

  function handleCellInput(event: React.KeyboardEvent<HTMLDivElement>) {
    if (!isCellWithinBounds(selectedPosition)) return;
    const row = rows[selectedPosition.rowIdx];
    if (isGroupRow(row)) return;
    const { key } = event;
    const column = columns[selectedPosition.idx];

    if (selectedPosition.mode === 'EDIT') {
      if (key === 'Enter') {
        // Custom editors can listen for the event and stop propagation to prevent commit
        commitEditorChanges();
        closeEditor();
      }
      return;
    }

    column.editorOptions?.onCellKeyDown?.(event);
    if (event.isDefaultPrevented()) return;

    if (isCellEditable(selectedPosition) && isDefaultCellInput(event)) {
      setSelectedPosition(({ idx, rowIdx }) => ({
        idx,
        rowIdx,
        key,
        mode: 'EDIT',
        row,
        originalRow: row
      }));
    }
  }

  function handleEditorRowChange(row: R, commitChanges?: boolean) {
    if (selectedPosition.mode === 'SELECT') return;
    if (commitChanges) {
      updateRow(getRawRowIdx(selectedPosition.rowIdx), row);
      closeEditor();
    } else {
      setSelectedPosition((position) => ({ ...position, row }));
    }
  }

  function handleOnClose(commitChanges?: boolean) {
    if (commitChanges) {
      commitEditorChanges();
    }
    closeEditor();
  }

  /**
   * utils
   */
  const isCellWithinBounds = useCallback(({ idx, rowIdx }: Position): boolean => {
    return rowIdx >= 0 && rowIdx < rows.length && idx >= minColIdx && idx < columns.length;
  }, [columns.length, rows.length]);

  const isCellSelectionWithinBounds = useCallback(({ rowStart, rowEnd, colStart, colEnd }: GridSelection): boolean => {
    return rowStart >= 0 && rowEnd < rows.length && colStart >= minSelIdx && colEnd < columns.length;
  }, [columns.length, rows.length]);

  const isCellEditable = useCallback((position: Position): boolean => {
    return (
      isCellWithinBounds(position) &&
      isSelectedCellEditable({ columns, rows, selectedPosition: position, isGroupRow })
    );
  }, [columns, isCellWithinBounds, isGroupRow, rows]);

  const closeEditor = useCallback(() => {
    if (selectedPosition.mode === 'SELECT') return;
    setSelectedPosition(({ idx, rowIdx }) => ({ idx: idx || 1, rowIdx, mode: 'SELECT' }));
    onSelectedCellChange?.({ rowIdx: selectedPosition.rowIdx, idx: 1 });
  }, [selectedPosition]);

  const updateRow = useCallback((rowIdx: number, row: R) => {
    if (typeof onRowsChange !== 'function') return;
    if (row === rawRows[rowIdx]) return;
    const updatedRows = [...rawRows];
    updatedRows[rowIdx] = row;
    onRowsChange(updatedRows, {
      indexes: [rowIdx],
      column: columns[selectedPosition.idx]
    });

    closeEditor();
  }, [closeEditor, columns, onRowsChange, rawRows, selectedPosition.idx]);

  const commitEditorChanges = useCallback(() => {
    if (
      columns[selectedPosition.idx]?.editor == null ||
      selectedPosition.mode === 'SELECT' ||
      selectedPosition.row === selectedPosition.originalRow
    ) {
      return;
    }

    const rowIdx = getRawRowIdx(selectedPosition.rowIdx);
    updateRow(rowIdx, selectedPosition.row);
  }, [columns, getRawRowIdx, selectedPosition, updateRow]);

  const selectCell = useCallback((position: Position, enableEditor?: boolean | null): void => {
    if (!isCellWithinBounds(position)) return;
    if (position.sel && !isCellSelectionWithinBounds(position.sel)) return;
    commitEditorChanges();

    if (enableEditor && (isCellEditable(position))) {
      const row = rows[position.rowIdx] as R;
      setSelectedPosition({ ...position, mode: 'EDIT', key: null, row, originalRow: row });
    } else {
      setSelectedPosition({ ...position, mode: 'SELECT' });
    }
    onSelectedCellChange?.({ ...position });
    setDraggedOverRange([]);

    focusSinkRef.current!.focus();
  }, [commitEditorChanges, isCellEditable, isCellSelectionWithinBounds, isCellWithinBounds, onSelectedCellChange, rows]);

  const handleDragEnd = useCallback((dragType) => {
    if (!dragPos.current || !onFill || !onRowsChange) return;

    const { column, startRowIdx, startIdx, selType, lastRowIdx, lastIdx } = dragPos.current;
    if (typeof lastRowIdx === 'undefined' || typeof lastIdx === 'undefined') return;
    const sourceRow = rawRows[startRowIdx];

    // VS legacy onFill dragging        
    if (selType === 'DRAG' || selType === 'REORDER') {
      const selRowIdx = dragType === 'row' ? lastRowIdx : startRowIdx;
      const selColIdx = dragType === 'col' || startIdx === -1 ? lastIdx : startIdx;
      onFill({
        fromRow: selType === 'REORDER' ? startRowIdx : Math.min(startRowIdx, selRowIdx),
        toRow: selType === 'REORDER' ? selRowIdx : Math.max(startRowIdx, selRowIdx),
        fromCol: selType === 'REORDER' ? 0 : Math.min(startIdx, selColIdx),
        toCol: selType === 'REORDER' ? 0 : Math.max(startIdx, selColIdx),
        direction: dragType,
        targetRows: [],
        sourceRow,
        columnKey: ""
      });
    }

    const startRowIndex = startRowIdx < lastRowIdx ? startRowIdx + 1 : lastRowIdx;
    const endRowIndex = startRowIdx < lastRowIdx ? lastRowIdx + 1 : startRowIdx;
    const targetRows = rawRows.slice(startRowIndex, endRowIndex);
    const updatedTargetRows = onFill({ columnKey: column.key, sourceRow, targetRows });
    const updatedRows = [...rawRows];
    const indexes: number[] = [];

    if (updatedTargetRows) {
      for (let i = startRowIndex; i < endRowIndex; i++) {
        const targetRowIdx = i - startRowIndex;
        if (updatedRows[i] !== updatedTargetRows[targetRowIdx]) {
          updatedRows[i] = updatedTargetRows[targetRowIdx];
          indexes.push(i);
        }
      }
    }

    if (indexes.length > 0) {
      onRowsChange(updatedRows, { indexes, column });
    }

  }, [onFill, onRowsChange, rawRows]);

  // Drag start and stop
  useEffect(() => {
    if (!dragPos.current) return;
    if (isDragging && dragStarted.current) return; // hack to avoid rerun drag init    

    const { column, startRowIdx, startIdx, lastRowIdx, lastIdx } = dragPos.current;

    if (isDragging) {
      let newPos;
      // Full row selection
      if (startIdx === 0) {
        newPos = {
          rowIdx: startRowIdx, idx: 0, sel: {
            rowStart: startRowIdx,
            rowEnd: startRowIdx,
            colStart: minSelIdx,
            colEnd: columns.length - 1
          }
        }
      }
      else newPos = { rowIdx: startRowIdx, idx: startIdx };
      if (newPos.sel) selectCell(newPos, column.editorOptions?.editOnClick);
      else selectCell(newPos);
      dragStarted.current = true;
    } else {
      if (typeof lastIdx !== "undefined" && typeof lastRowIdx !== "undefined") {
        setDraggedOverRange([]);
        const dragType = Math.abs(lastIdx - startIdx) > Math.abs(lastRowIdx - startRowIdx) ? 'col' : 'row';
        if (startIdx !== 0) {
          const sel = {
            rowStart: dragType === 'row' || dragPos.current.selType !== 'DRAG' ? Math.min(lastRowIdx, startRowIdx) : startRowIdx,
            rowEnd: dragType === 'row' || dragPos.current.selType !== 'DRAG' ? Math.max(lastRowIdx, startRowIdx) : startRowIdx,
            colStart: dragType === 'col' || dragPos.current.selType !== 'DRAG' ? Math.min(lastIdx, startIdx) : startIdx,
            colEnd: dragType === 'col' || dragPos.current.selType !== 'DRAG' ? Math.max(lastIdx, startIdx) : startIdx
          };
          selectCell({
            rowIdx: startRowIdx,
            idx: startIdx,
            sel
          });
        }
        handleDragEnd(dragType);
      }
      dragPos.current = undefined;
    }
  }, [columns.length, handleDragEnd, isDragging, selectCell])

  const handleFormatterRowChangeLatest = useLatestFunc(updateRow);

  const scrollToCell = useCallback(({ idx, rowIdx }: Partial<Position>): void => {
    const { current } = gridRef;
    if (!current) return;

    if (typeof idx === 'number' && idx > lastFrozenColumnIndex) {
      rowIdx ??= selectedPosition.rowIdx;
      if (!isCellWithinBounds({ rowIdx, idx })) return;
      const { clientWidth } = current;
      const column = columns[idx];
      const { left, width } = columnMetrics.get(column)!;
      let right = left + width;
      const row = rows[rowIdx];
      if (!isGroupRow(row)) {
        const colSpan = getColSpan(column, lastFrozenColumnIndex, { type: 'ROW', row });
        if (colSpan !== undefined) {
          const { left, width } = columnMetrics.get(columns[column.idx + colSpan - 1])!;
          right = left + width;
        }
      }

      const isCellAtLeftBoundary = left < scrollLeft + totalFrozenColumnWidth;
      const isCellAtRightBoundary = right > clientWidth + scrollLeft;
      if (isCellAtLeftBoundary) {
        current.scrollLeft = left - totalFrozenColumnWidth;
      } else if (isCellAtRightBoundary) {
        current.scrollLeft = right - clientWidth;
      }
    }

    if (typeof rowIdx === 'number') {
      const rowTop = getRowTop(rowIdx);
      const rowHeight = getRowHeight(rowIdx);
      if (rowTop < scrollTop) {
        // at top boundary, scroll to the row's top
        current.scrollTop = rowTop;
      } else if (rowTop + rowHeight > scrollTop + clientHeight) {
        // at bottom boundary, scroll the next row's top to the bottom of the viewport
        current.scrollTop = rowTop + rowHeight - clientHeight;
      }
    }
  }, [clientHeight, columnMetrics, columns, getRowHeight, getRowTop, gridRef, isCellWithinBounds, isGroupRow, lastFrozenColumnIndex, rows, scrollLeft, scrollTop, selectedPosition.rowIdx, totalFrozenColumnWidth]);

  const cellMouseDownHandler = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>, rowIdx: number, column: CalculatedColumn<R, SR>) => {
    event.preventDefault();
    if (event.buttons !== 1) return;

    const { idx } = column;
    const eventTarget = event.target as HTMLElement;
    let selType: TSelection = 'SELECT';
    if (eventTarget.id === 'rdg-drag-handle') selType = 'DRAG';
    else if (eventTarget.id === 'rdg-reorder-handle') selType = 'REORDER';
    dragPos.current = {
      startRowIdx: rowIdx, startIdx: idx, selType, column
    }

    window.addEventListener('mouseover', onMouseOver);
    window.addEventListener('mouseup', onMouseUp);

    dragStarted.current = false;
    setDragging(true);

    function onMouseOver(event: MouseEvent) {
      // Trigger onMouseup in edge cases where we release the mouse button but `mouseup` isn't triggered,
      // for example when releasing the mouse button outside the iframe the grid is rendered in.
      // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
      if (event.buttons !== 1) onMouseUp();
    }

    function onMouseUp() {
      window.removeEventListener('mouseover', onMouseOver);
      window.removeEventListener('mouseup', onMouseUp);
      if (!dragPos.current) setDraggedOverRange([]);
      if (updateScrollInterval.current) {
        clearInterval(updateScrollInterval.current);
        updateScrollInterval.current = undefined;
        progScroll.current = { top: 1, bottom: 1, left: 1, right: 1 };
      }
      setDragging(false);
    }
  }, []);

  function handleDoubleClick(event: React.MouseEvent<HTMLDivElement>) {
  }
  /*function handleDoubleClick(event: React.MouseEvent<HTMLDivElement>) {
    event.stopPropagation();
    if (!onFill || !onRowsChange) return;
   
    // HEAD START
    const { idx, rowIdx } = selectedPosition;
    const sourceRow = rawRows[rowIdx];
    const targetRows = rawRows.slice(rowIdx + 1);
    const column = columns[idx];
    const updatedTargetRows = onFill({ columnKey: column.key, sourceRow, targetRows });
    const updatedRows = [...rawRows];
    const indexes: number[] = [];
   
    for (let i = rowIdx + 1; i < updatedRows.length; i++) {
      const targetRowIdx = i - rowIdx - 1;
      if (updatedRows[i] !== updatedTargetRows[targetRowIdx]) {
        updatedRows[i] = updatedTargetRows[targetRowIdx];
        indexes.push(i);
      }
    }
   
    if (indexes.length > 0) {
      onRowsChange(updatedRows, { indexes, column });
    }
  } 
  // HEAD END
   
    const column = columns[selectedPosition.idx];
    const cellKey = column.key;
    const value = rawRows[selectedPosition.rowIdx][cellKey as keyof R];
   
    onRowsUpdate?.({
      cellKey,
      fromRow: selectedPosition.rowIdx,
      toRow: rawRows.length - 1,
      updated: { [cellKey]: value } as unknown as never,
      action: 'COLUMN_FILL'
    });
  }*/

  const handleHeaderCellClick = useCallback((idx: number) => {
    selectCell(
      {
        rowIdx: 0,
        idx: idx > 0 ? idx : 1,
        sel: {
          rowStart: 0, rowEnd: rowsCount - 1, colStart: idx === 0 ? 1 : idx, colEnd: idx === 0 ? columns.length - 1 : idx
        }
      }
    );
  }, [selectCell, rowsCount, columns]);

  function updateSelection(cse: string, row: number, col: number, sel: GridSelection | undefined): GridSelection {
    if (!sel) sel = { rowStart: row, rowEnd: row, colStart: col, colEnd: col };
    else sel = { ...sel };
    switch (cse) {
      case 'ArrowUp':
        if (sel.rowEnd > row) sel.rowEnd -= 1;
        else sel.rowStart -= 1;
        return sel;
      case 'ArrowDown':
        if (sel.rowStart < row) sel.rowStart += 1;
        else sel.rowEnd += 1;
        return sel;
      case 'ArrowLeft':
        if (sel.colEnd > col) sel.colEnd -= 1;
        else sel.colStart -= 1;
        return sel;
      case 'ArrowRight':
        if (sel.colStart < col) sel.colStart += 1;
        else sel.colEnd += 1;
        return sel;
      default:
        return sel;
    }
  }

  function getNextPosition(key: string, ctrlKey: boolean, shiftKey: boolean): Position {
    const { idx, rowIdx, sel } = selectedPosition;
    const row = rows[rowIdx];
    const isRowSelected = isCellWithinBounds(selectedPosition) && idx === -1;

    // If a group row is focused, and it is collapsed, move to the parent group row (if there is one).
    if (
      key === 'ArrowLeft' &&
      isRowSelected &&
      isGroupRow(row) &&
      !row.isExpanded &&
      row.level !== 0
    ) {
      let parentRowIdx = -1;
      for (let i = selectedPosition.rowIdx - 1; i >= 0; i--) {
        const parentRow = rows[i];
        if (isGroupRow(parentRow) && parentRow.id === row.parentId) {
          parentRowIdx = i;
          break;
        }
      }
      if (parentRowIdx !== -1) {
        return { idx, rowIdx: parentRowIdx };
      }
    }

    switch (key) {
      case 'ArrowUp':
        if (shiftKey) return { idx, rowIdx, sel: updateSelection(key, rowIdx, idx, sel) };
        return { idx, rowIdx: rowIdx - 1 };
      case 'ArrowDown':
        if (shiftKey) return { idx, rowIdx, sel: updateSelection(key, rowIdx, idx, sel) };
        return { idx, rowIdx: rowIdx + 1 };
      case 'ArrowLeft':
        if (shiftKey) return { idx, rowIdx, sel: updateSelection(key, rowIdx, idx, sel) };
        return { idx: idx - 1, rowIdx };
      case 'ArrowRight':
        if (shiftKey) return { idx, rowIdx, sel: updateSelection(key, rowIdx, idx, sel) };
        return { idx: idx + 1, rowIdx };
      case 'Tab':
        if (selectedPosition.idx === -1 && selectedPosition.rowIdx === -1) {
          return shiftKey
            ? { idx: columns.length - 1, rowIdx: rows.length - 1 }
            : { idx: 0, rowIdx: 0 };
        }
        return { idx: idx + (shiftKey ? -1 : 1), rowIdx };
      case 'Home':
        // If row is selected then move focus to the first row
        if (isRowSelected) return { idx, rowIdx: 0 };
        return ctrlKey ? { idx: 0, rowIdx: 0 } : { idx: 0, rowIdx };
      case 'End':
        // If row is selected then move focus to the last row.
        if (isRowSelected) return { idx, rowIdx: rows.length - 1 };
        return ctrlKey
          ? { idx: columns.length - 1, rowIdx: rows.length - 1 }
          : { idx: columns.length - 1, rowIdx };
      case 'PageUp': {
        const nextRowY = getRowTop(rowIdx) + getRowHeight(rowIdx) - clientHeight;
        return { idx, rowIdx: nextRowY > 0 ? findRowIdx(nextRowY) : 0 };
      }
      case 'PageDown': {
        const nextRowY = getRowTop(rowIdx) + clientHeight;
        return { idx, rowIdx: nextRowY < totalRowHeight ? findRowIdx(nextRowY) : rows.length - 1 };
      }
      default:
        return selectedPosition;
    }
  }

  function navigate(event: React.KeyboardEvent<HTMLDivElement>) {
    if (selectedPosition.mode === 'EDIT') {
      const onNavigation =
        columns[selectedPosition.idx].editorOptions?.onNavigation ?? onEditorNavigation;
      if (!onNavigation(event)) return;
    }
    const { key, shiftKey } = event;

    let mode = cellNavigationMode;
    if (key === 'Tab') {
      // If we are in a position to leave the grid, stop editing but stay in that cell
      if (
        canExitGrid({
          shiftKey,
          cellNavigationMode,
          columns,
          rowsCount: rows.length,
          selectedPosition
        })
      ) {
        commitEditorChanges();
        // Allow focus to leave the grid so the next control in the tab order can be focused
        return;
      }

      mode = cellNavigationMode === 'NONE' ? 'CHANGE_ROW' : cellNavigationMode;
    }

    // Do not allow focus to leave
    event.preventDefault();

    const ctrlKey = isCtrlKeyHeldDown(event);
    const nextPosition = getNextSelectedCellPosition({
      columns,
      colSpanColumns,
      rows,
      lastFrozenColumnIndex,
      cellNavigationMode: mode,
      currentPosition: selectedPosition,
      nextPosition: getNextPosition(key, ctrlKey, shiftKey),
      isCellWithinBounds,
      isGroupRow
    });
    selectCell(nextPosition);
  }

  function processHotkey(event: React.KeyboardEvent<HTMLDivElement>) {
    const { key, shiftKey } = event;
    const ctrlKey = isCtrlKeyHeldDown(event);
    event.preventDefault();

    const nextPos = { ...selectedPosition };
    if (ctrlKey && key === 'a') {
      nextPos.sel = { rowStart: 0, rowEnd: rows.length - 1, colStart: 0, colEnd: columns.length - 1 };
    } else if (ctrlKey && key === ' ') {
      nextPos.sel = { rowStart: 0, rowEnd: rows.length - 1, colStart: selectedPosition.idx, colEnd: selectedPosition.idx };
    } else if (shiftKey && key === ' ') {
      nextPos.sel = { rowStart: selectedPosition.rowIdx, rowEnd: selectedPosition.rowIdx, colStart: 1, colEnd: columns.length - 1 };
    } else if (ctrlKey && shiftKey) {
      if (!nextPos.sel) nextPos.sel = { rowStart: nextPos.rowIdx, rowEnd: nextPos.rowIdx, colStart: nextPos.idx, colEnd: nextPos.idx };
      switch (key) {
        case 'ArrowUp':
          nextPos.sel.rowStart = 0;
          break;
        case 'ArrowDown':
          nextPos.sel.rowEnd = rows.length - 1;
          break;
        case 'ArrowLeft':
          nextPos.sel.colStart = 1;
          break;
        case 'ArrowRight':
          nextPos.sel.colEnd = columns.length - 1;
          break;
        default:
          break;
      }
    } else if (ctrlKey) {
      switch (key) {
        case 'ArrowUp':
          nextPos.rowIdx = 0;
          break;
        case 'ArrowDown':
          nextPos.rowIdx = rows.length - 1;
          break;
        case 'ArrowLeft':
          nextPos.idx = 1;
          break;
        case 'ArrowRight':
          nextPos.idx = columns.length - 1;
          break;
        default:
          break;
      }
    }
    selectCell(nextPos);
  }

  const getDraggedOverCellsRange = useCallback((rowIdx: number) => {
    if (!dragPos.current) return;
    const { lastRowIdx, lastIdx, selType, startIdx, startRowIdx } = dragPos.current;
    if (typeof lastIdx === "undefined" || typeof lastRowIdx === "undefined") return;
    if (lastIdx < 1 || lastRowIdx < 0) return;

    let newRanges: (number[] | undefined)[] = [];
    const rowsIdxs: number[] = Array.from(new Array(Math.max(startRowIdx, lastRowIdx) - Math.min(startRowIdx, lastRowIdx) + 1), (x, i) => i + Math.min(startRowIdx, lastRowIdx));

    rowsIdxs.forEach((currentRowIdx) => {
      if (selType === 'DRAG' && Math.abs(startIdx - lastIdx)
        > Math.abs(startRowIdx - lastRowIdx)) {
        // drag across cols
        newRanges[currentRowIdx] = [startIdx, lastIdx].sort((a, b) => a - b);
      } else {
        // drag across rows
        const isDraggedOver = startRowIdx < lastRowIdx
          ? startRowIdx <= currentRowIdx && currentRowIdx <= lastRowIdx
          : startRowIdx >= currentRowIdx && currentRowIdx >= lastRowIdx;

        if (!isDraggedOver) return;
        if (selType === 'SELECT') newRanges[currentRowIdx] = [startIdx, lastIdx].sort((a, b) => a - b);
        if (selType === 'DRAG') newRanges[currentRowIdx] = [startIdx, startIdx];
      }
    });

    setDraggedOverRange(newRanges);
  }, []);

  const getSelectedCellsRange = function (rowIdx: number): number[] | undefined {
    const { sel } = selectedPosition;
    if (!sel) return undefined;
    if (sel.rowStart <= rowIdx && sel.rowEnd >= rowIdx) {
      return [sel.colStart, sel.colEnd];
    }
    return undefined;
  };

  function getSelectedCellProps(rowIdx: number): SelectedCellProps | EditCellProps<R> | undefined {
    if (selectedPosition.rowIdx !== rowIdx) return;

    if (selectedPosition.mode === 'EDIT') {
      return {
        mode: 'EDIT',
        idx: selectedPosition.idx,
        rowIdx: selectedPosition.rowIdx,
        onKeyDown: handleKeyDown,
        editorProps: {
          editorPortalTarget,
          row: selectedPosition.row,
          onRowChange: handleEditorRowChange,
          onClose: handleOnClose
        }
      };
    }

    return {
      mode: 'SELECT',
      idx: selectedPosition.idx,
      rowIdx: selectedPosition.rowIdx,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      //@ts-expect-error
      dragHandleProps:
        enableCellDragAndDrop && isCellEditable(selectedPosition)
          ? { onMouseDown: cellMouseDownHandler, onDoubleClick: handleDoubleClick }
          : undefined,
    };
  }

  function getViewportRows() {
    const rowElements = [];
    let startRowIndex = 0;
    for (let rowIdx = rowOverscanStartIdx; rowIdx <= rowOverscanEndIdx; rowIdx++) {
      const row = rows[rowIdx];
      const top = getRowTop(rowIdx) + headerRowHeight;
      if (isGroupRow(row)) {
        ({ startRowIndex } = row);
        const isGroupRowSelected =
          isSelectable && row.childRows.every((cr) => selectedRows?.has(rowKeyGetter!(cr)));
        rowElements.push(
          <GroupRowRenderer
            aria-level={row.level + 1} // aria-level is 1-based
            aria-setsize={row.setSize}
            aria-posinset={row.posInSet + 1} // aria-posinset is 1-based
            aria-rowindex={headerRowsCount + startRowIndex + 1} // aria-rowindex is 1 based
            aria-selected={isSelectable ? isGroupRowSelected : undefined}
            key={row.id}
            id={row.id}
            groupKey={row.groupKey}
            viewportColumns={viewportColumns}
            childRows={row.childRows}
            rowIdx={rowIdx}
            row={row}
            top={top}
            height={getRowHeight(rowIdx)}
            level={row.level}
            isExpanded={row.isExpanded}
            selectedCellIdx={selectedPosition.rowIdx === rowIdx ? selectedPosition.idx : undefined}
            isRowSelected={isGroupRowSelected}
            onFocus={selectedPosition.rowIdx === rowIdx ? handleFocus : undefined}
            onKeyDown={selectedPosition.rowIdx === rowIdx ? handleKeyDown : undefined}
            selectGroup={selectGroupLatest}
            toggleGroup={toggleGroupLatest}
          />
        );
        continue;
      }

      startRowIndex++;
      let key;
      let isRowSelected = false;
      if (typeof rowKeyGetter === 'function') {
        key = rowKeyGetter(row);
        isRowSelected = selectedRows?.has(key) ?? false;
      } else {
        key = hasGroups ? startRowIndex : rowIdx;
      }

      rowElements.push(
        <RowRenderer
          aria-rowindex={headerRowsCount + (hasGroups ? startRowIndex : rowIdx) + 1} // aria-rowindex is 1 based
          aria-selected={isSelectable ? isRowSelected : undefined}
          cellMouseDownHandler={cellMouseDownHandler}
          cellStyles={cellStyles?.[rowIdx]}
          draggedOverRange={draggedOverRange[rowIdx]}
          dragPos={dragPos}
          enableDrag={enableCellDragAndDrop}
          height={getRowHeight(rowIdx)}
          key={key}
          isRowSelected={isRowSelected}
          lastFrozenColumnIndex={lastFrozenColumnIndex}
          onRowDoubleClick={onRowDoubleClick}
          onRowChange={handleFormatterRowChangeLatest}
          onRowClick={onRowClick}
          reorderRows={reorderRows}
          row={row}
          rowClass={rowClass}
          rowIdx={rowIdx}
          selectCell={selectCellLatest}
          selectedCellProps={getSelectedCellProps(rowIdx)}
          selectedRange={getSelectedCellsRange(rowIdx)}
          top={top}
          updateDraggedOverRange={getDraggedOverCellsRange}
          viewportColumns={viewportColumns}
        />
      );
    }

    return rowElements;
  }

  // Reset the positions if the current values are no longer valid. This can happen if a column or row is removed
  if (selectedPosition.idx >= columns.length || selectedPosition.rowIdx >= rows.length) {
    setSelectedPosition(initialPosition);
    dragPos.current = undefined;
  }

  if (
    selectedPosition.mode === 'EDIT' &&
    rows[selectedPosition.rowIdx] !== selectedPosition.originalRow
  ) {
    // Discard changes if rows are updated from outside
    // We need to avoid this to keep row headers editable when a temp row becomes non temp and header is still being named
    //closeEditor();
  }

  return (
    <div
      role={hasGroups ? 'treegrid' : 'grid'}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-multiselectable={isSelectable ? true : undefined}
      aria-colcount={columns.length}
      aria-rowcount={headerRowsCount + rowsCount + summaryRowsCount}

      className={clsx(rootClassname, { [viewportDraggingClassname]: isDragging, 'rdg-viewport-dragging': dragPos.current?.selType === 'DRAG' }, className)}
      style={
        {
          ...style,
          '--header-row-height': `${headerRowHeight}px`,
          '--row-width': `${totalColumnWidth}px`,
          '--summary-row-height': `${summaryRowHeight}px`,
          ...layoutCssVars
        } as unknown as React.CSSProperties
      }
      ref={gridRef}
      onScroll={handleScroll}
    >
      <HeaderRow
        columns={viewportColumns}
        dragPos={dragPos}
        onColumnResize={handleColumnResize}
        allRowsSelected={allRowsSelected}
        onAllRowsSelectionChange={selectAllRowsLatest}
        sortColumns={sortColumns}
        onSortColumnsChange={onSortColumnsChange}
        lastFrozenColumnIndex={lastFrozenColumnIndex}
        handleHeaderCellClick={handleHeaderCellClick}
      />
      {rows.length === 0 && EmptyRowsRenderer ? (
        <EmptyRowsRenderer />
      ) : (
        <>
          {/*
            Ideally the focus should be set on the selected cell
            (https://www.w3.org/TR/wai-aria-practices-1.2/#kbd_roving_tabindex)
            but there are a few issues with this approach
            - onKeyDown/useLayoutEffect on the cell is not fast enough and grid
              looses focus if tab is pressed repeatedly
            - All the cells are not rendered so it is possible the focused cell
              is not in the viewport and unmounted and in this case the grid
              does not have any focusable element

            This first issue can be solved by setting onKeyDown on the root element
            but for the second issue we need to render the selected cell along with
            the cells in the viewport. This adds complexity.

            To circumvent this issue we are using an extra div element which is
            positioned on the top/left corner of the viewport and it listens for the
            keyboard event and sets the selected cell's position.
          */}
          <div
            ref={focusSinkRef}
            tabIndex={0}
            className={focusSinkClassname}
            onKeyDown={handleKeyDown}
          />
          <div style={{ height: max(totalRowHeight, clientHeight) }} />
          <RowSelectionChangeProvider value={selectRowLatest}>
            {getViewportRows()}
          </RowSelectionChangeProvider>
          {summaryRows?.map((row, rowIdx) => (
            <SummaryRow
              aria-rowindex={headerRowsCount + rowsCount + rowIdx + 1}
              key={rowIdx}
              rowIdx={rowIdx}
              row={row}
              bottom={summaryRowHeight * (summaryRows.length - 1 - rowIdx)}
              viewportColumns={viewportColumns}
              lastFrozenColumnIndex={lastFrozenColumnIndex}
            />
          ))}
        </>
      )}
    </div>
  );
}

export default forwardRef(DataGrid) as <R, SR = unknown, K extends Key = Key>(
  props: DataGridProps<R, SR, K> & RefAttributes<DataGridHandle>
) => JSX.Element;
